import { PublicKey } from "@solana/web3.js";
import Decimal from "decimal.js";

import { MayNumber } from "../types";
import { bigIntLamportsToUiAmount, findMinMax, lamportsToUiAmount, safeBigIntToNumber } from "./math";
import { DASH_NULL_VALUE, DISABLED_APY_BIGINT } from "../constants";

export type Nullable<T> = T | null | undefined;

export function formatOrDash<T, R>(
    values: Nullable<T> | Nullable<T>[],
    formatter: () => R
): R | typeof DASH_NULL_VALUE {
    const list = Array.isArray(values) ? values : [values];

    return list.every((v) => v != null) ? formatter() : DASH_NULL_VALUE;
}

export function formatNum(num: MayNumber, options?: { longThreshold?: number; customDecimals?: number }) {
    if (num === undefined || num === null) return "--";

    if (options?.customDecimals) {
        if (num > 0 && num < 1) {
            return num.toLocaleString([], {
                minimumSignificantDigits: options.customDecimals,
                maximumSignificantDigits: options.customDecimals
            });
        }

        return num.toLocaleString([], {
            minimumFractionDigits: options.customDecimals,
            maximumFractionDigits: options?.customDecimals
        });
    }

    if (num >= (options?.longThreshold ?? 10_000)) {
        return Intl.NumberFormat([], {
            notation: "compact",
            minimumSignificantDigits: 2,
            maximumSignificantDigits: 4
        }).format(num);
    }
    if (num > (options?.longThreshold ?? 1_000_000)) {
        return Intl.NumberFormat([], { notation: "compact" }).format(num);
    }

    if (num > 0 && num < 1) {
        return num.toLocaleString([], { minimumSignificantDigits: 2, maximumSignificantDigits: 2 });
    }

    return num.toLocaleString([], { minimumFractionDigits: 0, maximumFractionDigits: options?.customDecimals ?? 2 });
}

export function formatStatNum(num: MayNumber, options?: { symbol: string }) {
    const postFix = options?.symbol ? ` ${options?.symbol}` : "";
    return formatNum(num, { longThreshold: 100 }) + postFix;
}

export const USD_COMPACT_THRESHOLD = 10000;

export function formatUsd(
    num: MayNumber,
    options?: {
        compactThreshold?: number;
        showFullUsdValue?: boolean;
        minimumSignificantDigits?: number;
        maximumFractionDigits?: number;
        signDisplay?: boolean;
    }
) {
    if (num === undefined) return "--";
    const numAbs = Math.abs(num);
    const decimals = options?.maximumFractionDigits ?? 2;

    const locale = "en-US";

    if (options?.compactThreshold && numAbs >= options?.compactThreshold) {
        return new Intl.NumberFormat(locale, {
            style: "currency",
            currency: "USD",
            notation: "compact",
            minimumSignificantDigits: options?.minimumSignificantDigits ?? 2,
            maximumSignificantDigits: 4,
            signDisplay: options?.signDisplay ? "exceptZero" : undefined
        }).format(num);
    }

    if (options?.showFullUsdValue) {
        return new Intl.NumberFormat(locale, {
            style: "currency",
            currency: "USD",
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
            signDisplay: options?.signDisplay ? "exceptZero" : undefined
        }).format(num);
    }

    if (numAbs > USD_COMPACT_THRESHOLD) {
        const significantDigits = 4;
        return new Intl.NumberFormat(locale, {
            style: "currency",
            currency: "USD",
            notation: "compact",
            minimumSignificantDigits: significantDigits,
            maximumSignificantDigits: significantDigits,
            signDisplay: options?.signDisplay ? "exceptZero" : undefined
        }).format(num);
    }

    if (numAbs > 0 && numAbs < 1) {
        const minVisibleValue = 1 / 10 ** decimals;
        if (numAbs < minVisibleValue) return `≤$${minVisibleValue}`;

        return new Intl.NumberFormat(locale, {
            style: "currency",
            currency: "USD",
            maximumFractionDigits: decimals,
            minimumFractionDigits: decimals,
            signDisplay: options?.signDisplay ? "exceptZero" : undefined
        }).format(num);
    }

    return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: decimals,
        minimumFractionDigits: decimals,
        signDisplay: options?.signDisplay ? "exceptZero" : undefined
    }).format(num);
}

const MAX_ALLOWED_DECIMALS = 2;

type FormatTokenAmountOptions = {
    decimals?: number;
    symbol?: string;
    hideSymbol?: boolean;
    short?: boolean;
    compactLargeNumbers?: boolean;
    isRawAmount?: boolean;
    displayDecimals?: number;
    signed?: boolean;
};

type RawTokenAmountFormatInput = FormatTokenAmountOptions & {
    amount: bigint | null | undefined;
};

export function formatTokenAmount(input: RawTokenAmountFormatInput): string;
export function formatTokenAmount(num: number | null | undefined, options: FormatTokenAmountOptions): string;
export function formatTokenAmount(
    numOrInput: number | null | undefined | RawTokenAmountFormatInput,
    options?: FormatTokenAmountOptions
) {
    // Raw bigint form: convert lamports → UI amount, then format through the shared core.
    if (typeof numOrInput === "object" && numOrInput !== null && "amount" in numOrInput) {
        const { amount } = numOrInput;
        // A non-finite UI amount would throw in `new Decimal(...)`, so treat it as missing ("--").
        const uiAmount = amount == null ? undefined : bigIntLamportsToUiAmount(amount, numOrInput.decimals);
        const value = uiAmount == null || !Number.isFinite(Number(uiAmount)) ? undefined : new Decimal(uiAmount);
        return formatUiTokenAmount(value, numOrInput);
    }

    if (!options) return "--";

    // Number form: optionally treat the number as raw lamports first. A non-finite number (e.g. NaN)
    // would throw in `new Decimal(...)`, so treat it as missing and render the "--" placeholder.
    const num =
        options.isRawAmount && numOrInput != null ? lamportsToUiAmount(numOrInput, options.decimals) : numOrInput;
    const value = num == null || !Number.isFinite(num) ? undefined : new Decimal(num);
    return formatUiTokenAmount(value, options);
}

// Single formatting core shared by both the number and raw-bigint entry points. `displayDecimals`,
// when provided, sets the precision and overrides the default 2-decimal cap; otherwise the default
// caps at MAX_ALLOWED_DECIMALS so the common "1.23 USDC" rendering is preserved.
function formatUiTokenAmount(value: Decimal | undefined, options: FormatTokenAmountOptions): string {
    const symbol = options.symbol ?? "";
    const postFix = symbol && !options.hideSymbol ? ` ${symbol}` : "";
    if (value === undefined) return "--" + postFix;
    const prefix = options.signed && value.gt(0) ? "+" : "";

    const decimals =
        options.displayDecimals ?? Math.min(options.decimals ?? MAX_ALLOWED_DECIMALS, MAX_ALLOWED_DECIMALS);
    const minVisibleValue = new Decimal(1).div(new Decimal(10).pow(decimals));

    if (options.short && value.gt(0) && value.lt(minVisibleValue))
        return `${prefix}≤${minVisibleValue.toString()}${postFix}`;
    if (options.short && value.lt(0) && value.abs().lt(minVisibleValue))
        return `${prefix}≤${minVisibleValue.toString()}${postFix}`;

    if (value.gt(0) && value.lt(1)) {
        return formatSmallDecimals(value.toNumber()) + postFix;
    }

    if (options.compactLargeNumbers && value.abs().gt(10_000)) {
        const significantDigits = 4;
        return (
            prefix +
            new Intl.NumberFormat([], {
                notation: "compact",
                minimumSignificantDigits: significantDigits,
                maximumSignificantDigits: significantDigits
            }).format(value.toNumber()) +
            postFix
        );
    }

    return prefix + formatDecimalWithCommas(value, decimals) + postFix;
}

function formatDecimalWithCommas(decimal: Decimal, decimals: number) {
    const fixed = decimal.toDecimalPlaces(decimals).toFixed(decimals);
    const [whole, fraction] = fixed.split(".");
    const sign = whole.startsWith("-") ? "-" : "";
    const wholeAbs = sign ? whole.slice(1) : whole;
    const wholeWithCommas = wholeAbs.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    const trimmedFraction = fraction?.replace(/0+$/, "");
    return `${sign}${wholeWithCommas}${trimmedFraction ? `.${trimmedFraction}` : ""}`;
}

export function formatSmallDecimals(num: number) {
    const rawFormat = num.toLocaleString([], {
        minimumSignificantDigits: 3,
        maximumSignificantDigits: 3
    });
    const [whole, decimals] = rawFormat.split(".");
    if (!decimals) return rawFormat;

    let leadingZerosCount = 0;
    while (decimals[leadingZerosCount] === "0") {
        leadingZerosCount++;
    }

    if (leadingZerosCount < 3) return rawFormat;

    if (leadingZerosCount > 9) {
        return "0";
    }

    const subscriptNumber = String.fromCharCode(8320 + leadingZerosCount);

    const remainingFraction = decimals.slice(leadingZerosCount);
    return `${whole}.0${subscriptNumber}${remainingFraction}`;
}

export function formatPercent(
    num: MayNumber,
    options?: {
        skipMultiplication?: boolean;
        hideSymbol?: boolean;
        customDecimals?: number;
        includePlus?: boolean;
        minimumSignificantDigits?: number;
        maxPrecisionUi?: number; // ex 0.1 -> 0.1%
        customDigits?: number;
        formatUndefinedAsNA?: boolean;
        collapseNegatives?: boolean;
    }
) {
    const minimumSignificantDigits = options?.minimumSignificantDigits ?? 2;
    const maximumSignificantDigits = Math.max(2, minimumSignificantDigits);
    const percent = num && !options?.skipMultiplication ? num * 100 : num || 0;

    const isCompact = percent > 100_000;
    const symbolWithSpace = isCompact ? " %" : "%";
    const symbol = options?.hideSymbol ? "" : symbolWithSpace;

    const notation = isCompact ? "compact" : "standard";
    const prefix = options?.includePlus && num && num > 0 ? "+" : "";

    if (options?.formatUndefinedAsNA && num === undefined) {
        return prefix + "N/A";
    }

    if (percent > 0 && percent < 1 && options?.maxPrecisionUi && percent < options?.maxPrecisionUi) {
        return prefix + `≤${options?.maxPrecisionUi.toLocaleString([])}` + symbol;
    }

    if (options?.customDigits) {
        return prefix + formatFixedDigits(percent, options.customDigits) + symbol;
    }

    if (options?.customDecimals) {
        return (
            prefix +
            percent.toLocaleString([], {
                minimumFractionDigits: options.customDecimals,
                maximumFractionDigits: options?.customDecimals,
                notation
            }) +
            symbol
        );
    }

    if (percent > 0 && percent < 1) {
        return (
            prefix +
            percent.toLocaleString([], { minimumSignificantDigits, maximumSignificantDigits, notation }) +
            symbol
        );
    }

    //
    if (percent < 0 && !!options?.collapseNegatives) {
        return "<0" + symbol;
    }

    return (
        prefix + percent.toLocaleString([], { maximumFractionDigits: 2, minimumFractionDigits: 0, notation }) + symbol
    );
}

export function formatFixedDigits(num: number, totalDigits = 3) {
    if (!num) return "0";

    const absNum = Math.abs(num);
    const minValue = 1 / 10 ** (totalDigits - 1);

    // Handle large numbers
    if (absNum >= 10 ** totalDigits) {
        return Intl.NumberFormat([], {
            notation: "compact",
            maximumSignificantDigits: totalDigits
        }).format(num);
    }

    // Handle small numbers
    if (absNum > 0 && absNum < minValue) {
        return `<${minValue}`;
    }

    // For numbers less than 1, we want to count decimal places after the first non-zero digit
    if (absNum < 1) {
        return num.toLocaleString([], {
            maximumSignificantDigits: totalDigits,
            minimumSignificantDigits: 1
        });
    }

    // Handle normal range
    return num.toLocaleString([], {
        minimumSignificantDigits: totalDigits,
        maximumSignificantDigits: totalDigits
    });
}

// Token symbols can contain URL-hostile chars (e.g. "usd.tel" — dots break SPA rewrites). Use for route params, compare slug-to-slug.
export function tokenSymbolToUrlSlug(symbol: string | undefined | null) {
    if (!symbol) return "";
    return symbol.replace(/[^\w-]/g, "-");
}

export function formatAddress(address: string | PublicKey | undefined | null, size = 4) {
    if (!address) return "--";
    return address.toString().slice(0, size) + "…" + address.toString().slice(-size);
}

export function formatStrategyAddress(address: string | PublicKey | undefined | null, size = 4) {
    if (!address) return "--";
    return address.toString().slice(0, size);
}

type FormatMinMaxOptions = {
    symbol: string;
    fallback?: string;
    formatter?: (val: number | undefined) => string;
    includeSpace?: boolean;
};
export function formatMinMax({
    min,
    max,
    symbol,
    fallback,
    formatter: formatterOverride,
    includeSpace
}: {
    min: number | string | undefined;
    max: number | string | undefined;
} & FormatMinMaxOptions) {
    const isPercent = symbol === "%";
    const symbolWithSpace = isPercent ? symbol : " " + symbol;

    const format = (inputVal: number | undefined) => {
        if (formatterOverride) {
            return formatterOverride(inputVal);
        }
        if (isPercent) {
            return formatPercent(inputVal, { hideSymbol: true });
        }
        return formatNum(inputVal);
    };

    const [fMin, fMax] = [typeof min === "string" ? min : format(min), typeof max === "string" ? max : format(max)];

    if (!min && !max) {
        if (fallback) return fallback;
        return `--${symbolWithSpace}`;
    }

    const space = includeSpace ? " " : "";
    if (min && !max) return fMin + symbolWithSpace;
    if (!min && max) return fMax + symbolWithSpace;
    if (min === max) return fMin + symbolWithSpace;
    return `${fMin}${space}-${space}${fMax}${symbolWithSpace}`;
}

export function formatMinMaxArray({ values, ...options }: { values: number[] | undefined } & FormatMinMaxOptions) {
    const { min, max } = findMinMax(values, (num) => num);
    return formatMinMax({ min, max, ...options });
}

export function formatPercentArray(values: number[] | undefined) {
    return formatMinMaxArray({
        values,
        formatter: (v) => formatPercent(v, { hideSymbol: true }),
        symbol: "%",
        fallback: "0%"
    });
}

export function snakeCaseToTitleCase(input: string, delimiter = "_") {
    // Handle empty input
    if (!input) {
        return "";
    }

    // Split the string into words using underscore as a delimiter
    const words = input.split(delimiter);

    // Capitalize the first letter of each word
    const titleCaseWords = words.map((word) => word.charAt(0).toUpperCase() + word.slice(1));

    // Join the words back together with a space
    const titleCaseString = titleCaseWords.join(" ");

    return titleCaseString.replace("Url", "URL").replace("Id", "ID");
}

export function formatMultiplier(multiplier: number | undefined, options?: { showNa?: boolean }) {
    if (!multiplier) return options?.showNa ? "N/A" : "--";
    return `${formatNum(multiplier, { customDecimals: 1 })}x`;
}

export function formatBigInt(num: bigint | undefined, formatter: (num: number) => string) {
    if (num === DISABLED_APY_BIGINT) return "Disabled";
    if (num === undefined) return "--";
    return formatter(safeBigIntToNumber(num));
}
export function formatBigIntPercent(num: bigint | undefined, options?: { hideSymbol?: boolean }) {
    if (num === DISABLED_APY_BIGINT) return "Disabled";
    if (num === undefined) return "--";
    return formatBigIntPercentInternal(num, options);
}

const TEN = BigInt(10);
const CBPS_SCALE = TEN ** BigInt(4); // 10_000

function formatBigIntPercentInternal(
    value: bigint | undefined,
    options?: {
        skipMultiplication?: boolean; // ignored (bigint already scaled)
        hideSymbol?: boolean;
        customDecimals?: number;
        includePlus?: boolean;
        minimumSignificantDigits?: number; // limited bigint support
        maxPrecisionUi?: number; // only used for comparison boundary
        customDigits?: number; // maps to fixed decimals
        formatUndefinedAsNA?: boolean;
        collapseNegatives?: boolean;
    }
) {
    if (options?.formatUndefinedAsNA && value === undefined) {
        return "N/A";
    }

    if (value === undefined) {
        value = BigInt(0);
    }

    if (value === DISABLED_APY_BIGINT) {
        return "Disabled";
    }

    const isNegative = value < BigInt(0);

    if (isNegative && options?.collapseNegatives) {
        return "<0" + (options?.hideSymbol ? "" : "%");
    }

    const abs = isNegative ? -value : value;

    const integer = abs / CBPS_SCALE;
    const remainder = abs % CBPS_SCALE;

    const decimals = options?.customDigits ?? options?.customDecimals ?? 2;

    let decimalString = remainder.toString().padStart(4, "0").slice(0, decimals);

    // Trim trailing zeros unless explicitly fixed
    if (!options?.customDigits && !options?.customDecimals) {
        decimalString = decimalString.replace(/0+$/, "");
    }

    const sign = isNegative ? "-" : options?.includePlus && value > BigInt(0) ? "+" : "";

    const symbol = options?.hideSymbol ? "" : "%";

    if (decimalString.length > 0) {
        return `${sign}${integer.toString()}.${decimalString}${symbol}`;
    }

    return `${sign}${integer.toString()}${symbol}`;
}
